import { FeatureFlagEnvironment } from 'lib_ts/enums/feature-flags.enums';
import { Controller } from 'lib_ts/enums/routing/controllers';
import { PlatformConsoleRoutes } from 'lib_ts/enums/routing/routes/platform-console';
import { IServerResponse } from 'lib_ts/interfaces/common/i-server-response';
import {
  IFeatureFlag,
  IFeatureFlagAuditLog,
  IFeatureFlagCreateRequest,
  IFeatureFlagUpdateRequest,
} from 'lib_ts/interfaces/feature-flags/i-feature-flag';
import { BaseRESTService } from 'services/_base-rest.service';

export class PlatformConsoleService extends BaseRESTService {
  private static instance: PlatformConsoleService;
  static getInstance(): PlatformConsoleService {
    if (!PlatformConsoleService.instance) {
      PlatformConsoleService.instance = new PlatformConsoleService();
    }

    return PlatformConsoleService.instance;
  }

  private constructor() {
    super({
      controller: Controller.PlatformConsole,
    });
  }

  /** server-authoritative confirmation that the user may access the console */
  async checkAccess(): Promise<boolean> {
    return await this.get({
      uri: PlatformConsoleRoutes.AccessCheck,
    })
      .then((result) => result?.success === true)
      .catch(() => false);
  }

  // --- feature flag management (staff only) --------------------------------

  async listFeatureFlags(
    environment: FeatureFlagEnvironment
  ): Promise<IFeatureFlag[]> {
    const result: IServerResponse = await this.get({
      uri: PlatformConsoleRoutes.FeatureFlags,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      params: { environment } as any,
    });

    return result?.success ? ((result.data as IFeatureFlag[]) ?? []) : [];
  }

  /** single flag by id (drives the standalone detail page) */
  async getFeatureFlag(id: string): Promise<IFeatureFlag | undefined> {
    const result: IServerResponse = await this.get({
      uri: `${PlatformConsoleRoutes.FeatureFlags}/${id}`,
    });

    return result?.success ? (result.data as IFeatureFlag) : undefined;
  }

  async createFeatureFlag(
    payload: IFeatureFlagCreateRequest
  ): Promise<IServerResponse> {
    return await this.post(
      { uri: PlatformConsoleRoutes.FeatureFlags },
      payload
    );
  }

  async updateFeatureFlag(
    id: string,
    payload: IFeatureFlagUpdateRequest
  ): Promise<IServerResponse> {
    return await this.patch(
      { uri: `${PlatformConsoleRoutes.FeatureFlags}/${id}` },
      payload
    );
  }

  async deleteFeatureFlag(id: string): Promise<IServerResponse> {
    return await this.delete({
      uri: `${PlatformConsoleRoutes.FeatureFlags}/${id}`,
    });
  }

  /** copy an existing flag into another environment */
  async duplicateFeatureFlag(
    id: string,
    environment: FeatureFlagEnvironment
  ): Promise<IServerResponse> {
    return await this.post(
      { uri: `${PlatformConsoleRoutes.FeatureFlags}/${id}/duplicate` },
      { environment }
    );
  }

  /** read-only audit trail for a single flag in an environment */
  async listFeatureFlagAudit(
    flagName: string,
    environment: FeatureFlagEnvironment
  ): Promise<IFeatureFlagAuditLog[]> {
    const result: IServerResponse = await this.get({
      uri: `${PlatformConsoleRoutes.FeatureFlags}/audit`,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      params: { flagName, environment } as any,
    });

    return result?.success
      ? ((result.data as IFeatureFlagAuditLog[]) ?? [])
      : [];
  }
}
